/* eslint-disable max-lines */
/* eslint-disable max-len */
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import EXPIRY_OPTIONS from 'common/constants/tagExpiryOptions';
import AutoCompleteMultipleSelect from 'common/forms/AutoCompleteMultipleSelect';
import { addDays, addHours, endOfDay, format, startOfDay } from 'date-fns';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { bulkUpsertLineItemTags, listLineItemTags } from 'store/thunks/tagThunks';
import { sortTags } from 'common/utils';
import SYSTEM_TAG_NAMES from 'common/constants/systemTags';

const BulkManageTagsDialog = ({ open, onClose, selectedLineItems, onSuccess }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [showAllItems, setShowAllItems] = useState(false);
  const [selectedTagsWithExpiry, setSelectedTagsWithExpiry] = useState([]);
  const [tagResetCount, setTagResetCount] = useState(0);
  const [expiryPopoverState, setExpiryPopoverState] = useState(null);
  const [expiryDurationValue, setExpiryDurationValue] = useState('');
  const [customExpiryDate, setCustomExpiryDate] = useState(null);
  const [initialCommonTagNames, setInitialCommonTagNames] = useState([]);

  const allItemTags = useSelector(({ systemConfigs }) => systemConfigs.lineItemTags || []);
  // Exclude system-managed tags from the selectable options — users must not add/remove them
  const systemItemTags = allItemTags.filter((tag) => !SYSTEM_TAG_NAMES.includes(tag));

  const formatTagText = useCallback((tag) => {
    const tagName = typeof tag === 'string' ? tag : tag?.tagName || '';
    if (!tagName) return '';
    return tagName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }, []);

  const unFormatTagText = (formattedTag) => {
    return formattedTag
      .split(' ')
      .map((word) => word.toUpperCase())
      .join('_');
  };

  const formattedTagOptions = systemItemTags.map(formatTagText);

  useEffect(() => {
    if (systemItemTags.length === 0) {
      dispatch(listLineItemTags());
    }
  }, [dispatch, systemItemTags.length]);

  const handleClose = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      onClose();
    }
  };

  const toggleShowAllItems = () => {
    setShowAllItems((prev) => !prev);
  };

  const getExpiryOptionValue = (expiryDate) => {
    if (!expiryDate) return '';
    const now = new Date();
    const diffMs = expiryDate.getTime() - now.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    const tolerance = 1 / 60;
    const matchingOption = EXPIRY_OPTIONS.find((option) => {
      if (!option.hours && !option.days) return false;
      const expectedHours = option.hours ?? option.days * 24;
      return Math.abs(diffHours - expectedHours) < tolerance;
    });
    return matchingOption?.value ?? '';
  };

  const handleExpiryClick = (event, index) => {
    setExpiryPopoverState({ anchor: event.currentTarget, tagIndex: index });
    const currentExpiry = selectedTagsWithExpiry[index]?.expiry;
    setExpiryDurationValue(getExpiryOptionValue(currentExpiry) || '');
    const optionVal = getExpiryOptionValue(currentExpiry);
    if (currentExpiry && !optionVal) {
      setCustomExpiryDate(currentExpiry);
    } else {
      setCustomExpiryDate(null);
    }
  };

  const handleExpiryClose = () => {
    setExpiryPopoverState(null);
    setExpiryDurationValue('');
    setCustomExpiryDate(null);
  };

  const handleExpiryDateChange = (date, index) => {
    setCustomExpiryDate(date);
    setExpiryDurationValue('');
    setSelectedTagsWithExpiry((prev) => {
      const updated = [...prev];
      if (date) {
        const expiryAtEndOfDay = endOfDay(date);
        updated[index] = {
          ...updated[index],
          expiry: expiryAtEndOfDay,
        };
      } else {
        const { expiry, ...rest } = updated[index];
        updated[index] = rest;
      }
      return updated;
    });
  };

  const handleExpiryChange = (optionValue, index) => {
    setExpiryDurationValue(optionValue);
    setCustomExpiryDate(null);
    setSelectedTagsWithExpiry((prev) => {
      const updated = [...prev];
      if (!optionValue) {
        const { expiry, ...rest } = updated[index];
        updated[index] = rest;
        return updated;
      }

      const selectedOption = EXPIRY_OPTIONS.find((opt) => opt.value === optionValue);
      if (!selectedOption) {
        return updated;
      }

      const now = new Date();
      const expiryDate = selectedOption.hours
        ? addHours(now, selectedOption.hours)
        : addDays(now, selectedOption.days);

      updated[index] = {
        ...updated[index],
        expiry: expiryDate,
      };
      return updated;
    });
  };

  const handleResetExpiry = () => {
    setExpiryDurationValue('');
    setCustomExpiryDate(null);
    if (expiryPopoverState?.tagIndex != null) {
      handleExpiryChange('', expiryPopoverState.tagIndex);
    }
  };

  const handleTagSelectionChange = (selectedFormattedTags) => {
    const selectedTagNames = selectedFormattedTags.map(unFormatTagText);
    const updatedTags = selectedTagNames.map((tagName) => {
      const existing = selectedTagsWithExpiry.find((t) => t.tagName === tagName);
      return (
        existing ?? {
          tagName,
          formattedTagName: formatTagText({ tagName }),
        }
      );
    });

    setSelectedTagsWithExpiry(updatedTags);
  };

  const handleRemoveTag = (tagName, form) => {
    const updatedTags = selectedTagsWithExpiry.filter((tag) => tag.tagName !== tagName);
    setSelectedTagsWithExpiry(updatedTags);
    const updatedFormattedTags = updatedTags.map((tag) => tag.formattedTagName);
    form.change('tags', updatedFormattedTags);
    if (updatedTags.length === 0) {
      setTagResetCount((prev) => prev + 1);
    }
  };

  const handleSaveTags = () => {
    const tagsToSave = selectedTagsWithExpiry.map((tag) => ({
      tagName: tag.tagName,
      ...(tag.expiry && { expiry: tag.expiry.toISOString() }),
    }));

    const lineItemUpdates = selectedLineItems.map((item) => {
      const currentTags = item.tags || [];
      const currentTagNames = currentTags.map((tag) => tag.tagName || '');
      const newTagNames = tagsToSave.map((tag) => tag.tagName);
      const addTags = newTagNames.filter((tagName) => {
        if (!currentTagNames.includes(tagName)) return true;
        const currentTag = currentTags.find((t) => t.tagName === tagName);
        const newTag = tagsToSave.find((t) => t.tagName === tagName);
        const currentExpiry = currentTag?.expiry
          ? new Date(currentTag.expiry).getTime()
          : undefined;
        const newExpiry = newTag?.expiry ? new Date(newTag.expiry).getTime() : undefined;
        return currentExpiry !== newExpiry;
      });
      const removeTags = initialCommonTagNames.filter((tagName) => !newTagNames.includes(tagName));

      return {
        lineItemId: item.lineItemId,
        mpi: item.mpi,
        currentTags,
        newTags: tagsToSave,
        addTags: addTags.length > 0 ? addTags : undefined,
        removeTags: removeTags.length > 0 ? removeTags : undefined,
      };
    });

    const hasExpirySet = tagsToSave.some((tag) => tag.expiry != null);
    const hasExpiryChanges = lineItemUpdates.some((update) => {
      const currentTags = update.currentTags || [];
      const newTags = update.newTags || [];
      return newTags.some((newTag) => {
        const currentTag = currentTags.find((tag) => tag.tagName === newTag.tagName);
        if (!currentTag) return false;
        const currentExpiry = currentTag.expiry;
        const newExpiry = newTag.expiry;
        const currentExpiryTime = currentExpiry ? new Date(currentExpiry).getTime() : undefined;
        const newExpiryTime = newExpiry ? new Date(newExpiry).getTime() : undefined;
        return currentExpiryTime !== newExpiryTime;
      });
    });

    const itemsToUpdate = lineItemUpdates.filter(
      (update) =>
        (update.addTags && update.addTags.length > 0) ||
        (update.removeTags && update.removeTags.length > 0) ||
        hasExpirySet ||
        hasExpiryChanges
    );

    if (itemsToUpdate.length === 0) {
      enqueueSnackbar('No changes to save', { variant: 'info' });
      return Promise.resolve();
    }

    return dispatch(bulkUpsertLineItemTags({ lineItemUpdates: itemsToUpdate }))
      .then((result) => {
        if (result) {
          enqueueSnackbar(`Successfully updated tags for ${itemsToUpdate.length} item(s)`, {
            variant: 'success',
          });
          onSuccess();
        } else {
          enqueueSnackbar('Failed to update tags', { variant: 'error' });
        }
      })
      .catch(() => {
        enqueueSnackbar('Error updating tags', { variant: 'error' });
      });
  };

  const initialValues = useMemo(() => {
    const commonTags = systemItemTags.filter((tag) => {
      return selectedLineItems.every((item) => {
        const itemTags = item.tags || [];
        const itemTagNames = itemTags.map((t) => t.tagName || '');
        return itemTagNames.includes(tag);
      });
    });

    const commonFormattedTags = commonTags.map(formatTagText);

    return {
      tags: commonFormattedTags,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) {
      const commonTags = systemItemTags.filter((tag) => {
        return selectedLineItems.every((item) => {
          const itemTags = item.tags || [];
          const itemTagNames = itemTags.map((t) => t.tagName || '');
          return itemTagNames.includes(tag);
        });
      });

      const normalizedTags = commonTags.map((tag) => ({
        tagName: tag,
        formattedTagName: formatTagText(tag),
      }));
      setSelectedTagsWithExpiry(normalizedTags);
      setInitialCommonTagNames(commonTags);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const displayedItems = showAllItems ? selectedLineItems : selectedLineItems.slice(0, 5);
  const hasMoreItems = selectedLineItems.length > 5;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby='BulkManageTags-dialog-header'
      fullWidth
      maxWidth='md'
    >
      <DialogTitle id='BulkManageTags-dialog-header'>
        Bulk Manage Tags - {selectedLineItems.length} Item(s) Selected
      </DialogTitle>

      <DialogContent>
        <Form
          initialValues={initialValues}
          onSubmit={handleSaveTags}
          render={({ handleSubmit, submitting, form }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Grid container direction='column' spacing={2}>
                <Grid item>
                  <Alert severity='info' sx={{ mb: 1 }}>
                    Select tags to apply to all {selectedLineItems.length} selected item(s). Tags
                    will be added or removed as needed.
                  </Alert>
                </Grid>

                <Grid item>
                  {selectedTagsWithExpiry.length > 0 && (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          mb: 1,
                          p: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                        }}
                      >
                        {selectedTagsWithExpiry.map((tag, index) => (
                          <Chip
                            key={tag.tagName}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant='body2'>{tag.formattedTagName}</Typography>
                                {tag.expiry && (
                                  <Typography variant='caption' color='textSecondary'>
                                    ({format(tag.expiry, 'MM/dd/yyyy HH:mm')})
                                  </Typography>
                                )}
                              </Box>
                            }
                            onDelete={() => handleRemoveTag(tag.tagName, form)}
                            deleteIcon={<CloseIcon />}
                            size='small'
                            sx={{ maxWidth: '100%' }}
                            icon={
                              <IconButton
                                size='small'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExpiryClick(e, index);
                                }}
                                sx={{ p: 0.25 }}
                              >
                                <CalendarTodayIcon fontSize='small' />
                              </IconButton>
                            }
                          />
                        ))}
                      </Box>

                      {selectedTagsWithExpiry.some((tag) => Boolean(tag.expiry)) && (
                        <Typography variant='caption' color='red' sx={{ display: 'block', mb: 2 }}>
                          * Times shown next to a tag indicate its expiry. Once the time elapses,
                          the tag expires automatically.
                        </Typography>
                      )}
                    </>
                  )}

                  <AutoCompleteMultipleSelect
                    key={tagResetCount}
                    name='tags'
                    label='Select Tags for All Items'
                    id='bulkManageTags-tags-field'
                    options={formattedTagOptions}
                    sortOptions
                    freeSolo={false}
                    disabled={submitting}
                    defaultValue={selectedTagsWithExpiry.map((tag) => tag.formattedTagName)}
                    validations={[]}
                    onChange={(_, value) => handleTagSelectionChange(value, form)}
                  />
                  <Typography variant='caption' color='textSecondary' sx={{ mt: 1 }}>
                    Select tags to apply to all selected items
                  </Typography>
                </Grid>

                <Grid item>
                  <Divider />
                </Grid>

                <Grid item>
                  <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
                    Selected Items
                  </Typography>
                  <TableContainer component={Paper} variant='outlined'>
                    <Table size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell>Order ID</TableCell>
                          <TableCell>Patient</TableCell>
                          <TableCell>Drug Name</TableCell>
                          <TableCell>Current Tags</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayedItems.map((item) => {
                          const rawTags = item.tags
                            ? sortTags(item.tags).filter(
                                (tag) => tag?.tagName !== 'BACKFILLED_SHIP_BY'
                              )
                            : [];
                          // System tags first, then user tags
                          const itemTags = [
                            ...rawTags.filter((tag) => tag.isSystemTag),
                            ...rawTags.filter((tag) => !tag.isSystemTag),
                          ];

                          return (
                            <TableRow key={item.lineItemId} hover>
                              <TableCell>
                                <Typography variant='body2'>{item.orderId}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant='body2'>{item.patientName}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant='body2'>{item.medicationName}</Typography>
                              </TableCell>
                              <TableCell>
                                {!isEmpty(itemTags) ? (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {itemTags.map((tag) => (
                                      <Chip
                                        key={tag.tagName || ''}
                                        label={formatTagText(tag)}
                                        size='small'
                                        sx={
                                          tag.isSystemTag ? { opacity: 0.7, cursor: 'default' } : {}
                                        }
                                      />
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography variant='caption' color='textSecondary'>
                                    No tags
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {hasMoreItems && (
                    <Box sx={{ mt: 1, textAlign: 'center' }}>
                      <Button size='small' onClick={toggleShowAllItems}>
                        {showAllItems
                          ? 'Show Less'
                          : `Show ${selectedLineItems.length - 5} More Item(s)`}
                      </Button>
                    </Box>
                  )}
                </Grid>
              </Grid>

              <DialogActions sx={{ mt: 3, px: 0 }}>
                <Button
                  onClick={handleClose}
                  variant='outlined'
                  color='secondary'
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <LoadingButton
                  variant='contained'
                  color='primary'
                  loading={submitting}
                  disabled={submitting}
                  type='submit'
                >
                  Apply Tags to All Items
                </LoadingButton>
              </DialogActions>
            </form>
          )}
        />

        <Popover
          open={Boolean(expiryPopoverState?.anchor)}
          anchorEl={expiryPopoverState?.anchor}
          onClose={(e, reason) => {
            if (reason !== 'backdropClick') handleExpiryClose();
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, minWidth: 300 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant='subtitle2'>Set Expiry Date & Time</Typography>
              <Tooltip title='Reset' arrow placement='left'>
                <IconButton size='small' onClick={handleResetExpiry} aria-label='Reset expiry'>
                  <RestoreIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            </Box>
            {expiryPopoverState?.tagIndex != null && (
              <>
                <FormControl fullWidth size='small' sx={{ mt: 1 }}>
                  <InputLabel id='expiry-duration-label'>Expiry Duration</InputLabel>
                  <Select
                    labelId='expiry-duration-label'
                    id='expiry-duration-select'
                    value={expiryDurationValue}
                    label='Expiry Duration'
                    onChange={(e) =>
                      handleExpiryChange(e.target.value, expiryPopoverState.tagIndex)
                    }
                  >
                    {EXPIRY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography
                  variant='caption'
                  color='textSecondary'
                  sx={{ mt: 1, display: 'block' }}
                >
                  Or choose a date (from next day — expiry at end of day):
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    minDate={startOfDay(addDays(new Date(), 1))}
                    value={
                      customExpiryDate ||
                      (selectedTagsWithExpiry[expiryPopoverState?.tagIndex]?.expiry
                        ? startOfDay(
                            new Date(selectedTagsWithExpiry[expiryPopoverState.tagIndex].expiry)
                          )
                        : null)
                    }
                    onChange={(date) => handleExpiryDateChange(date, expiryPopoverState.tagIndex)}
                    label='Expiry date (end of day)'
                    renderInput={(params) => (
                      <TextField
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...params}
                        size='small'
                        fullWidth
                        sx={{ mt: 1 }}
                        inputProps={{ ...params.inputProps, readOnly: true }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </>
            )}
            {expiryPopoverState?.tagIndex != null &&
              selectedTagsWithExpiry[expiryPopoverState.tagIndex]?.expiry && (
                <Typography
                  variant='caption'
                  color='textSecondary'
                  sx={{ mt: 1, display: 'block' }}
                >
                  Expires:{' '}
                  {format(
                    selectedTagsWithExpiry[expiryPopoverState.tagIndex].expiry,
                    'MM/dd/yyyy HH:mm'
                  )}
                </Typography>
              )}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button size='small' variant='outlined' onClick={handleExpiryClose}>
                Close
              </Button>
              <Button size='small' variant='contained' color='primary' onClick={handleExpiryClose}>
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>
      </DialogContent>
    </Dialog>
  );
};

BulkManageTagsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedLineItems: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string.isRequired,
      orderId: PropTypes.string.isRequired,
      mpi: PropTypes.string.isRequired,
      patientName: PropTypes.string.isRequired,
      medicationName: PropTypes.string.isRequired,
      quantity: PropTypes.number,
      tags: PropTypes.arrayOf(
        PropTypes.shape({
          tagName: PropTypes.string.isRequired,
          expiry: PropTypes.string,
          createdAt: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default BulkManageTagsDialog;
